import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import logger from '@agoras/logger';
import { mergeStyleSets, FontWeights, NeutralColors, PrimaryButton, Panel, Stack, Text, MessageBar, MessageBarType, IconButton, TextField, Checkbox, Separator, Link } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useState, useContext, useCallback } from 'react';
import { headerContext } from '../../../context.esm.js';
import { PRIVACY_STATEMENT_LINK_URL } from './constants.esm.js';
import { validateEmail } from './utils.esm.js';
import ConfirmationModal from './ConfirmationModal.esm.js';
import { AppContext } from '../../../../context/context.esm.js';
import { useUserQuery } from '../../../../../hooks/useUserQuery.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var classNames = mergeStyleSets({
    subTitle: {
        fontWeight: FontWeights.semibold
    },
    iconFont: {
        fontSize: 32
    },
    textField: {
        height: 100
    }
});
var iconButtonStyles = {
    root: {
        color: NeutralColors.gray160,
        boxSizing: 'content-box',
        padding: 12
    },
    rootDisabled: {
        backgroundColor: 'unset'
    },
    rootHovered: {
        color: NeutralColors.gray160
    },
    rootChecked: {
        color: NeutralColors.gray160
    },
    rootCheckedHovered: {
        color: NeutralColors.gray160
    },
    rootPressed: {
        color: NeutralColors.gray160
    },
    rootCheckedPressed: {
        color: NeutralColors.gray160
    }
};
var FeedbackPanel = function(param) {
    var isOpen = param.isOpen, dismissPanel = param.dismissPanel;
    var _useState = _sliced_to_array(useState(false), 2), isConfirmed = _useState[0], setIsConfirmed = _useState[1];
    var _useContext = useContext(AppContext), signedIn = _useContext.user.signedIn, unifiedExperience = _useContext.unifiedExperience;
    var email = useUserQuery().email;
    var storefront = unifiedExperience ? 'Marketplace' : 'AppSource';
    var _useState1 = _sliced_to_array(useState(true), 2), positiveFeedback = _useState1[0], setPositiveFeedback = _useState1[1];
    var _useState2 = _sliced_to_array(useState(''), 2), textField = _useState2[0], setTextField = _useState2[1];
    var _useState3 = _sliced_to_array(useState(''), 2), guestEmailAddress = _useState3[0], setGuestEmailAddress = _useState3[1];
    var _useState4 = _sliced_to_array(useState(false), 2), contactMeChecked = _useState4[0], setContactMeChecked = _useState4[1];
    var t = useTranslation('header').t;
    var _useState5 = _sliced_to_array(useState(false), 2), browsingThroughAppSourceChecked = _useState5[0], setBrowsingThroughAppSourceChecked = _useState5[1];
    var _useState6 = _sliced_to_array(useState(false), 2), findingAnAppChecked = _useState6[0], setFindingAnAppChecked = _useState6[1];
    var _useState7 = _sliced_to_array(useState(false), 2), installingAnAppChecked = _useState7[0], setInstallingAnAppChecked = _useState7[1];
    var emailAddressTextFieldDisabled = !contactMeChecked || signedIn;
    var emailAddressErrorMessage = !validateEmail(guestEmailAddress) && !emailAddressTextFieldDisabled ? t('Invalid email address') : undefined;
    var handleDismissPanel = useCallback(function() {
        setPositiveFeedback(true);
        setTextField('');
        setGuestEmailAddress('');
        setContactMeChecked(false);
        setBrowsingThroughAppSourceChecked(false);
        setFindingAnAppChecked(false);
        setInstallingAnAppChecked(false);
        dismissPanel();
    }, [
        dismissPanel
    ]);
    var handleSubmitFeedback = useCallback(function() {
        var feedbackResult = {
            happyExperience: positiveFeedback,
            browseContribution: browsingThroughAppSourceChecked,
            installContribution: installingAnAppChecked,
            findContribution: findingAnAppChecked,
            comments: textField,
            email: contactMeChecked ? email || guestEmailAddress : '',
            authenticatedUser: signedIn
        };
        logger.info(JSON.stringify(feedbackResult), _object_spread({
            action: 'Feedback',
            actionModifier: 'Info',
            collection: 'Feedback'
        }, headerContext));
        logger.info('', _object_spread({
            action: 'Feedback',
            actionModifier: 'Submitted'
        }, headerContext));
        handleDismissPanel();
        setIsConfirmed(true);
    }, [
        browsingThroughAppSourceChecked,
        positiveFeedback,
        installingAnAppChecked,
        findingAnAppChecked,
        textField,
        contactMeChecked,
        email,
        guestEmailAddress,
        signedIn,
        handleDismissPanel
    ]);
    var onRenderFooterContent = useCallback(function() {
        return /*#__PURE__*/ jsx(PrimaryButton, {
            onClick: handleSubmitFeedback,
            disabled: Boolean(emailAddressErrorMessage),
            "data-bi-id": "CTA_SubmitFeedback",
            "data-bi-area": "Feedback",
            children: t('Send feedback')
        });
    }, [
        handleSubmitFeedback,
        emailAddressErrorMessage,
        t
    ]);
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(Panel, {
                headerText: t("We'd love your feedback"),
                isOpen: isOpen,
                onDismiss: function() {
                    logger.info('', _object_spread({
                        action: 'Feedback',
                        actionModifier: 'Dismissed'
                    }, headerContext));
                    handleDismissPanel();
                },
                closeButtonAriaLabel: "Close",
                isLightDismiss: true,
                isFooterAtBottom: true,
                onRenderFooterContent: onRenderFooterContent,
                children: /*#__PURE__*/ jsxs(Stack, {
                    tokens: {
                        childrenGap: 16
                    },
                    children: [
                        /*#__PURE__*/ jsx(Text, {
                            className: classNames.subTitle,
                            children: t('Thank you for taking the time to give us feedback!')
                        }),
                        /*#__PURE__*/ jsx(MessageBar, {
                            messageBarType: MessageBarType.info,
                            children: t("For specific concerns or issues regarding your use of a product, please contact the publisher via the support link on the app's product page")
                        }),
                        /*#__PURE__*/ jsx(Text, {
                            className: classNames.subTitle,
                            children: t('Let us know how you feel')
                        }),
                        /*#__PURE__*/ jsxs(Stack, {
                            horizontal: true,
                            children: [
                                /*#__PURE__*/ jsx(IconButton, {
                                    iconProps: {
                                        iconName: 'Emoji2',
                                        styles: {
                                            root: {
                                                fontSize: 32
                                            }
                                        }
                                    },
                                    checked: positiveFeedback,
                                    ariaLabel: t('Positive'),
                                    styles: iconButtonStyles,
                                    onClick: function() {
                                        return setPositiveFeedback(true);
                                    }
                                }),
                                /*#__PURE__*/ jsx(IconButton, {
                                    iconProps: {
                                        iconName: 'Sad',
                                        styles: {
                                            root: {
                                                fontSize: 32
                                            }
                                        }
                                    },
                                    checked: !positiveFeedback,
                                    ariaLabel: t('Negative'),
                                    styles: iconButtonStyles,
                                    onClick: function() {
                                        return setPositiveFeedback(false);
                                    }
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx(TextField, {
                            placeholder: t('Tell us more about your experience'),
                            multiline: true,
                            resizable: false,
                            styles: {
                                fieldGroup: {
                                    height: 80
                                }
                            },
                            value: textField,
                            onChange: function(_, value) {
                                return setTextField(value || '');
                            }
                        }),
                        /*#__PURE__*/ jsx(Text, {
                            className: classNames.subTitle,
                            children: t('Did any of these factors contribute toward your experience?')
                        }),
                        /*#__PURE__*/ jsxs(Stack, {
                            tokens: {
                                childrenGap: 16
                            },
                            children: [
                                /*#__PURE__*/ jsx(Checkbox, {
                                    label: t('Browsing through Storefront', {
                                        storefront: storefront
                                    }),
                                    checked: browsingThroughAppSourceChecked,
                                    onChange: function(_, checked) {
                                        return setBrowsingThroughAppSourceChecked(checked);
                                    }
                                }),
                                /*#__PURE__*/ jsx(Checkbox, {
                                    label: t('Finding a product'),
                                    checked: findingAnAppChecked,
                                    onChange: function(_, checked) {
                                        return setFindingAnAppChecked(checked);
                                    }
                                }),
                                /*#__PURE__*/ jsx(Checkbox, {
                                    label: t('Installing a product'),
                                    checked: installingAnAppChecked,
                                    onChange: function(_, checked) {
                                        return setInstallingAnAppChecked(checked);
                                    }
                                })
                            ]
                        }),
                        /*#__PURE__*/ jsx(Separator, {}),
                        /*#__PURE__*/ jsx(Checkbox, {
                            label: t("It's okay to contact me about the feedback."),
                            checked: contactMeChecked,
                            onChange: function(_, checked) {
                                return setContactMeChecked(checked);
                            }
                        }),
                        /*#__PURE__*/ jsx(TextField, {
                            disabled: emailAddressTextFieldDisabled,
                            placeholder: t('email address'),
                            value: signedIn ? email : guestEmailAddress,
                            onChange: function(_, value) {
                                return setGuestEmailAddress(value || '');
                            },
                            type: "email",
                            errorMessage: emailAddressErrorMessage
                        }),
                        /*#__PURE__*/ jsx(MessageBar, {
                            messageBarType: MessageBarType.info,
                            children: t('When you send this feedback, an email message will be sent to the Storefront team. We may reach out to you for further information.', {
                                storefront: storefront
                            })
                        }),
                        /*#__PURE__*/ jsx(Link, {
                            href: PRIVACY_STATEMENT_LINK_URL,
                            rel: "noreferrer",
                            target: "_blank",
                            children: t('Privacy Statement')
                        })
                    ]
                })
            }),
            /*#__PURE__*/ jsx(ConfirmationModal, {
                isOpen: isConfirmed,
                onDismiss: function() {
                    setIsConfirmed(false);
                }
            })
        ]
    });
};

export { FeedbackPanel as default };
